import { isNull } from 'lodash-es';
import type { ComponentType, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import type { InternalUrl, Query } from './internal-url';

export type TransitionOptions = {
  scroll?: boolean;
};

export type LinkProps = Omit<React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'href' | 'ref'> & {
  href: string | InternalUrl;
  options?: TransitionOptions;
};

export type Link = ComponentType<LinkProps>;

export type Routing = {
  Link: Link;

  pathname: string;
  query: Query;

  back: () => void;

  push: (url: string | InternalUrl, options?: TransitionOptions) => void;
  pushWithAdditionalQueryParams: (params: Query, options?: TransitionOptions) => void;
  pushWithoutQueryParams: (paramNames: (keyof Query)[], options?: TransitionOptions) => void;

  replace: (url: string | InternalUrl, options?: TransitionOptions) => void;
  replaceWithAdditionalQueryParams: (params: Query, options?: TransitionOptions) => void;
  replaceWithoutQueryParams: (paramNames: (keyof Query)[], options?: TransitionOptions) => void;

  isActiveRoute: (url: string | InternalUrl) => boolean;
};

const RoutingContext = createContext<Routing | null>(null);

type RoutingProviderProps = {
  routing: Routing;
};

export const RoutingProvider = ({ children, routing }: PropsWithChildren<RoutingProviderProps>) => (
  <RoutingContext.Provider value={routing}>
    {children}
  </RoutingContext.Provider>
);

export const useRouting = () => {
  const routing = useContext(RoutingContext);

  if (isNull(routing)) {
    throw new Error('No routing provided.');
  }

  return routing;
};

export const useLink = () => {
  const { Link } = useRouting();
  return Link;
};
